import i18next from 'i18next'
import * as yup from 'yup'

import type { TranslatedValues } from 'shared/lib'
import { HEX_COLOR_PATTERN } from 'shared/lib/utils/color'

import { CMS_SOCIAL_PLATFORMS } from './consts'
import type { CMSSocialNetwork } from './types'

const SLUG_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/
const URL_SLUG_REGEX = /^[a-z0-9]+(?:[/-][a-z0-9]+)*$/

// --- Publications ---

export const cmsPublicationSchema = () =>
    yup.object().shape({
        name: yup.string().required(i18next.t('cms:validation.name-required')),
        slug: yup
            .string()
            .optional()
            .nullable()
            .test(
                'slug-format',
                i18next.t('cms:validation.slug-format'),
                (value) => {
                    if (!value) return true
                    return SLUG_REGEX.test(value)
                }
            ),
        cmsPublicationCategoryId: yup
            .string()
            .required(i18next.t('cms:validation.category-required')),
        metaTitle: yup
            .string()
            .optional()
            .nullable()
            .max(META_TITLE_MAX, i18next.t('cms:validation.meta-title-max')),
        metaDescription: yup
            .string()
            .optional()
            .nullable()
            .max(
                META_DESCRIPTION_MAX,
                i18next.t('cms:validation.meta-description-max')
            ),
    })

export const cmsPublicationDefaultValues = {
    name: '',
    mainImageKey: '',
    slug: '',
    data: [] as import('./types').CMSBlock[],
    cmsPublicationCategoryId: '',
    sportIds: [] as string[],
    competitionIds: [] as string[],
    cmsEventIds: [] as string[],
    metaTitle: '',
    metaDescription: '',
    ogImageKey: '',
    /** UI-only: current status for display; never sent on create, may be sent on update */
    status: 'draft' as import('./types').CMSStatus,
}

// --- Events ---

const META_TITLE_MAX = 256
const META_DESCRIPTION_MAX = 512

export const cmsEventSchema = () =>
    yup.object().shape({
        name: yup.string().required(i18next.t('cms:validation.name-required')),
        title: yup
            .string()
            .required(i18next.t('cms:validation.title-required')),
        startDate: yup
            .string()
            .required(i18next.t('cms:validation.start-date-required')),
        endDate: yup
            .string()
            .required(i18next.t('cms:validation.end-date-required')),
        backgroundColor: yup
            .string()
            .required(i18next.t('cms:validation.background-color-required')),
        slug: yup
            .string()
            .optional()
            .nullable()
            .max(256, i18next.t('cms:validation.slug-max'))
            .test(
                'slug-format',
                i18next.t('cms:validation.slug-format'),
                (value) => {
                    if (!value) return true
                    return SLUG_REGEX.test(value)
                }
            ),
        metaTitle: yup
            .string()
            .optional()
            .nullable()
            .max(META_TITLE_MAX, i18next.t('cms:validation.meta-title-max')),
        metaDescription: yup
            .string()
            .optional()
            .nullable()
            .max(
                META_DESCRIPTION_MAX,
                i18next.t('cms:validation.meta-description-max')
            ),
    })

export const cmsEventDefaultValues = {
    name: '',
    title: '',
    subtitle: '',
    mainImageKey: '',
    startDate: new Date().toISOString(),
    endDate: new Date().toISOString(),
    location: '',
    backgroundColor: '#ffffff',
    slug: '',
    cmsPublicationCategoryId: '',
    metaTitle: '',
    metaDescription: '',
    ogImageKey: '',
    data: [] as import('./types').CMSBlock[],
    /** UI-only: current status for display; never sent on create */
    status: 'draft' as import('./types').CMSStatus,
}

// --- Pages ---

export const cmsPageSchema = () =>
    yup.object().shape({
        name: yup.string().required(i18next.t('cms:validation.name-required')),
        // A non-clickable page is a pure navigation container (e.g. "Projekti")
        // with no public route, so its url may be empty (backend stores null).
        url: yup.string().when('isClickable', {
            is: (clickable: boolean) => clickable === false,
            then: (schema) =>
                schema.matches(URL_SLUG_REGEX, {
                    message: i18next.t('cms:validation.url-format'),
                    excludeEmptyString: true,
                }),
            otherwise: (schema) =>
                schema
                    .required(i18next.t('cms:validation.url-required'))
                    .matches(
                        URL_SLUG_REGEX,
                        i18next.t('cms:validation.url-format')
                    ),
        }),
        backgroundColor: yup
            .string()
            .required(i18next.t('cms:validation.background-color-required')),
        isClickable: yup.boolean().required(),
        showInNavigation: yup.boolean().required(),
        sectionsAsSubpages: yup.boolean().required(),
        layout: yup
            .mixed<'default' | 'o-nama' | 'o-hassu'>()
            .oneOf(['default', 'o-nama', 'o-hassu'])
            .optional(),
        metaTitle: yup
            .string()
            .optional()
            .nullable()
            .max(META_TITLE_MAX, i18next.t('cms:validation.meta-title-max')),
        metaDescription: yup
            .string()
            .optional()
            .nullable()
            .max(
                META_DESCRIPTION_MAX,
                i18next.t('cms:validation.meta-description-max')
            ),
    })

export const cmsPageDefaultValues = {
    name: '',
    url: '',
    showInNavigation: false,
    sectionsAsSubpages: false,
    isClickable: true,
    backgroundColor: '#ffffff',
    layout: 'default' as import('./types').CMSPageLayout,
    metaTitle: '',
    metaDescription: '',
    ogImageKey: '',
    data: [] as import('./types').CMSBlock[],
    parentPageId: null as string | null,
    /** Ordered header-dropdown items shown under this page */
    subpages: [] as import('./types').CMSNavItem[],
    /** UI-only: current status for display; never sent on create */
    status: 'draft' as import('./types').CMSStatus,
}

// --- Document Categories ---

export const cmsDocumentCategorySchema = () =>
    yup.object().shape({
        name: yup.string().required(i18next.t('cms:validation.name-required')),
    })

export const cmsDocumentCategoryDefaultValues = {
    name: '',
    status: 'active' as import('./types').CMSDocumentStatus,
}

// --- Sponsors (name + link url) ---

export const cmsSponsorSchema = () =>
    yup.object().shape({
        name: yup.string().required(i18next.t('cms:validation.name-required')),
        link: yup.string().url(i18next.t('cms:validation.url-invalid')),
    })

// --- Settings ---

export const cmsSettingsSchema = () =>
    yup.object().shape({
        email: yup.string().email(i18next.t('cms:validation.email-format')),
        themeColor: yup.string().matches(HEX_COLOR_PATTERN, {
            message: i18next.t('cms:validation.hex-color'),
            excludeEmptyString: true,
        }),
    })

const emptySocialNetworks = (): CMSSocialNetwork[] =>
    CMS_SOCIAL_PLATFORMS.map((p) => ({
        logoImage: p.logoImage,
        name: p.defaultName,
        url: '',
        isVisible: false,
    }))

export const cmsSettingsDefaultValues = {
    logoHeaderImageKey: '',
    logoHeaderImageName: '',
    logoFooterImageKey: '',
    faviconImageKey: '',
    faviconImageName: '',
    webclipImageKey: '',
    webclipImageName: '',
    themeColor: '',
    email: '',
    phone: '',
    location: '',
    /** Translated `location` per language, saved in settings `translations` */
    locationTranslations: {} as TranslatedValues,
    oib: '',
    mb: '',
    iban: '',
    organizationLinks: [],
    aboutUsLinks: [],
    userZoneLinks: [],
    socialNetworks: emptySocialNetworks(),
    partners: [],
    memberCategories: [],
    institutionMembers: [],
}
